import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex, FormControl, FormErrorMessage, Heading, Input, Switch, Text, theme } from '@chakra-ui/core';
import { Column, CellProps } from 'react-table';
import isString from 'lodash/isString';
import { AutomationConfiguration, JobPosition } from '../../PositionType';
import colors from '../../../../../styles/colors';
import { clearErrorMessage, FormattedError } from '../../../../../utils/FormErrorUtils';
import { useStoreState } from '../../../../../models/hooks';
import { DataTable } from '../../../accountParameter/components/DataTable';
import { AutomationTypes } from '../AutomationTypes';
import { usePositionParametersFormFields, FieldConfig } from './usePositionParametersFormFields';
import i18n from '../../../../../locales/i18n';
import { translateDecimalSymbolAccordingToLanguage } from '../../../../../utils/uiUtils';

type Props = {
  jobPositionData: JobPosition;
  setJobPositionData: React.Dispatch<React.SetStateAction<JobPosition>>;
  formErrors: FormattedError[];
  setFormErrors: React.Dispatch<React.SetStateAction<FormattedError[]>>;
};

type FormState = {
  automateHiringAfterOffer: boolean;
  maxBookmarkAge: string;
  overbookingFactor: string;
  maxLimitInterviews: string;
  shouldOverlap: boolean;
  requisitionActiveThresholdDays: string;
};

const PositionParametersContext = React.createContext<{
  formState: FormState;
  formErrors: FormattedError[];
  handleFieldChange: (key: keyof FormState, value: string | boolean) => void;
  isFrench: boolean;
} | null>(null);

const ValueCell: React.FC<CellProps<FieldConfig>> = ({ row }) => {
  const context = React.useContext(PositionParametersContext);
  // istanbul ignore next
  if (!context) {
    return null;
  }
  const { formState, formErrors, handleFieldChange, isFrench } = context;

  const field = row.original;
  const error = formErrors.find((err) => err.label === field.key)?.message;

  if (field.type === 'switch') {
    return (
      <FormControl>
        <Flex justifyContent="flex-start" alignItems="center" h="100%">
          <Switch
            id={field.key}
            isChecked={formState[field.key]}
            onChange={(e) => handleFieldChange(field.key, e.target.checked)}
            colorScheme="blue"
            size="sm"
            data-testid={`position-param-switch-${field.key}`}
          />
        </Flex>
      </FormControl>
    );
  }

  if (field.type === 'numeric') {
    const isOverbookingFactor = field.key === 'overbookingFactor';
    const displayValue =
      isFrench && isOverbookingFactor
        ? translateDecimalSymbolAccordingToLanguage(String(formState[field.key]), i18n.language)
        : formState[field.key];

    let pattern = '[0-9]*';
    if (isOverbookingFactor) {
      pattern = isFrench ? '[0-9]*[,]?[0-9]*' : '[0-9]*[.]?[0-9]*';
    }

    return (
      <FormControl isInvalid={!!error}>
        <Flex alignItems="center" justifyContent="flex-start">
          <Input
            id={field.key}
            value={displayValue as string}
            onChange={(e) => {
              let { value } = e.target;
              if (isFrench && isOverbookingFactor) {
                value = translateDecimalSymbolAccordingToLanguage(value, 'en') as string;
              }
              handleFieldChange(field.key, value);
            }}
            inputMode={isOverbookingFactor ? 'decimal' : 'numeric'}
            pattern={pattern}
            variant="filled"
            bg="white"
            _focus={{
              backgroundColor: colors.gray['150'],
              border: '1px solid',
              borderColor: colors.blue[500],
              boxShadow: `0 0 0 1px ${colors.blue[500]}`,
            }}
            data-testid={`position-param-input-${field.key}`}
            w="70px"
            textAlign="right"
          />
          <Text fontSize="sm" color="gray.600" ml={4} minW="120px">
            {field.unit ? `${field.unit} (${field.min}-${field.max})` : `(${field.min}-${field.max})`}
          </Text>
        </Flex>
        <FormErrorMessage data-testid={`position-param-error-${field.key}`}>{error}</FormErrorMessage>
      </FormControl>
    );
  }

  return null;
};

const LabelCell: React.FC<CellProps<FieldConfig>> = ({ row }) => <Text fontWeight="bold">{row.original.label}</Text>;
LabelCell.displayName = 'LabelCell';

const getFormStateFromProps = (data: JobPosition): FormState => {
  const initialData = {
    automateHiringAfterOffer: !!data.automateHiringAfterOffer,
    maxBookmarkAge: String(data.automationConfiguration?.maxBookmarkAgeInDays ?? ''),
    overbookingFactor: String(data.automationConfiguration?.overbookingFactor ?? ''),
    maxLimitInterviews: String(data.maxLimitInterviews ?? ''),
    shouldOverlap: !!data.shouldOverlap,
    requisitionActiveThresholdDays: String(data.requisitionActiveThresholdDays ?? ''),
  };
  return initialData;
};

const parseNumeric = (value: string): number | null => {
  if (value.trim() === '') return null;
  return Number(value);
};

const NUMERIC_PATTERN = /^\d*$/;
const DECIMAL_PATTERN = /^\d*\.?\d*$/;

const PositionParametersView: React.FC<Props> = ({ jobPositionData, setJobPositionData, formErrors, setFormErrors }) => {
  const { t } = useTranslation('createJobPosition');
  const isFrench = i18n.language === 'fr';
  const FORM_FIELDS_CONFIG = usePositionParametersFormFields();
  const accountAutomateHiringAfterOffer = useStoreState((s) => s.app.accounts?.configuration?.automateHiringAfterOffer);
  const isRequisitionEnabled = useStoreState((state) => state.app.accounts?.configuration?.requisitionEnabled);

  const initialFormState = React.useMemo(() => getFormStateFromProps(jobPositionData), [jobPositionData]);
  const [originalState] = React.useState(() => getFormStateFromProps(jobPositionData));
  const [formState, setFormState] = React.useState<FormState>(initialFormState);

  const isAutomationScreeningOrScheduling =
    jobPositionData.automationType === AutomationTypes.SCREENING || jobPositionData.automationType === AutomationTypes.SCHEDULING;

  const isVirtualRectuiterEnabled = jobPositionData.virtualRecruiterEnabled;

  const activeFields = React.useMemo(() => {
    return FORM_FIELDS_CONFIG.filter((field) => {
      if (field.key === 'automateHiringAfterOffer') {
        return accountAutomateHiringAfterOffer;
      }

      if (field.key === 'requisitionActiveThresholdDays') {
        return isRequisitionEnabled;
      }

      if (field.key === 'maxBookmarkAge' || field.key === 'overbookingFactor' || field.key === 'shouldOverlap') {
        if (field.key === 'shouldOverlap') return false; // todo: shouldOverlap is temporarily hidden for now, later we can remove this code block
        return isAutomationScreeningOrScheduling || isVirtualRectuiterEnabled;
      }
      return true;
    });
  }, [
    FORM_FIELDS_CONFIG,
    accountAutomateHiringAfterOffer,
    isAutomationScreeningOrScheduling,
    isVirtualRectuiterEnabled,
    isRequisitionEnabled,
  ]);

  React.useEffect(() => {
    setFormState(initialFormState);
  }, [initialFormState]);

  React.useEffect(() => {
    const isAutomationActive = isAutomationScreeningOrScheduling || isVirtualRectuiterEnabled;
    const revertPayload: Partial<JobPosition> = {};
    const revertAutomationPayload: Partial<AutomationConfiguration> = {};
    let needsUpdate = false;

    if (!accountAutomateHiringAfterOffer) {
      if (jobPositionData.automateHiringAfterOffer !== originalState.automateHiringAfterOffer) {
        revertPayload.automateHiringAfterOffer = originalState.automateHiringAfterOffer;
        needsUpdate = true;
      }
    }

    if (!isAutomationActive) {
      if (jobPositionData.shouldOverlap !== originalState.shouldOverlap) {
        revertPayload.shouldOverlap = originalState.shouldOverlap;
        needsUpdate = true;
      }

      const currentMaxBookmarkAge = String(jobPositionData.automationConfiguration?.maxBookmarkAgeInDays ?? '');
      if (currentMaxBookmarkAge !== originalState.maxBookmarkAge) {
        revertAutomationPayload.maxBookmarkAgeInDays =
          originalState.maxBookmarkAge === '' ? undefined : parseNumeric(originalState.maxBookmarkAge);
        needsUpdate = true;
      }

      const currentOverbookingFactor = String(jobPositionData.automationConfiguration?.overbookingFactor ?? '');
      if (currentOverbookingFactor !== originalState.overbookingFactor) {
        revertAutomationPayload.overbookingFactor =
          originalState.overbookingFactor === '' ? undefined : parseNumeric(originalState.overbookingFactor);
        needsUpdate = true;
      }
    }

    if (needsUpdate) {
      if (Object.keys(revertAutomationPayload).length > 0) {
        revertPayload.automationConfiguration = {
          ...jobPositionData.automationConfiguration,
          ...revertAutomationPayload,
        };
      }
      setJobPositionData((prev) => ({ ...prev, ...revertPayload }));
    }
  }, [
    isAutomationScreeningOrScheduling,
    isVirtualRectuiterEnabled,
    accountAutomateHiringAfterOffer,
    jobPositionData,
    originalState,
    setJobPositionData,
  ]);

  const handleFieldChange = React.useCallback(
    (key: keyof FormState, value: string | boolean) => {
      if (formErrors.length > 0) {
        setFormErrors(clearErrorMessage(formErrors, key));
      }
      let isValid = true;
      if (isString(value)) {
        const pattern = key === 'overbookingFactor' ? DECIMAL_PATTERN : NUMERIC_PATTERN;
        if (!pattern.test(value)) {
          isValid = false;
        }
      }
      if (isValid) {
        setFormState((prev) => ({ ...prev, [key]: value }));
      }
    },
    [formErrors, setFormErrors],
  );

  React.useEffect(() => {
    const hasChanges = activeFields.some(({ key }) => formState[key] !== initialFormState[key]);
    if (!hasChanges) {
      return;
    }

    const handler = setTimeout(() => {
      const changedValues: Partial<JobPosition> = {};
      const changedAutomationConfig: Partial<AutomationConfiguration> = {};

      if (formState.automateHiringAfterOffer !== initialFormState.automateHiringAfterOffer) {
        changedValues.automateHiringAfterOffer = formState.automateHiringAfterOffer;
      }
      // istanbul ignore next
      if (formState.shouldOverlap !== initialFormState.shouldOverlap) {
        changedValues.shouldOverlap = formState.shouldOverlap;
      }
      if (formState.maxLimitInterviews !== initialFormState.maxLimitInterviews) {
        changedValues.maxLimitInterviews = parseNumeric(formState.maxLimitInterviews);
      }
      if (formState.requisitionActiveThresholdDays !== initialFormState.requisitionActiveThresholdDays) {
        changedValues.requisitionActiveThresholdDays = parseNumeric(formState.requisitionActiveThresholdDays);
      }
      if (formState.maxBookmarkAge !== initialFormState.maxBookmarkAge) {
        changedAutomationConfig.maxBookmarkAgeInDays = parseNumeric(formState.maxBookmarkAge);
      }
      if (formState.overbookingFactor !== initialFormState.overbookingFactor) {
        if (!formState.overbookingFactor.endsWith('.')) {
          changedAutomationConfig.overbookingFactor = parseNumeric(formState.overbookingFactor);
        }
      }

      if (Object.keys(changedAutomationConfig).length > 0) {
        changedValues.automationConfiguration = {
          ...jobPositionData.automationConfiguration,
          ...changedAutomationConfig,
        };
      }

      if (Object.keys(changedValues).length > 0) {
        setJobPositionData((prevData) => ({
          ...prevData,
          ...changedValues,
        }));
      }
    }, 5);

    // eslint-disable-next-line consistent-return
    return () => {
      clearTimeout(handler);
    };
  }, [formState, initialFormState, jobPositionData, setJobPositionData, activeFields]);

  const data = activeFields;

  const columns = React.useMemo<Column<FieldConfig>[]>(
    () => [
      {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        Header: t('positionParameters.settingColumn') as string,
        accessor: 'label',
        Cell: LabelCell,
        width: '25%',
      },
      {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        Header: t('positionParameters.descriptionColumn') as string,
        accessor: 'description',
        width: '45%',
      },
      {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        Header: t('positionParameters.valueColumn') as string,
        accessor: 'key',
        disableSortBy: true,
        width: '30%',
        Cell: ValueCell,
      },
    ],
    [t],
  );

  const contextValue = React.useMemo(() => ({ formState, formErrors, handleFieldChange, isFrench }), [
    formState,
    formErrors,
    handleFieldChange,
    isFrench,
  ]);

  return (
    <Box width="100%">
      <Flex align="center" mb={6} px={6}>
        <Box>
          <Heading as="h3" fontSize="md" lineHeight="1.3" mb={1}>
            {t('positionParameters.header')}
          </Heading>
          <Text fontSize="xs" color="gray.500" lineHeight="normal">
            {t('positionParameters.description')}
          </Text>
        </Box>
      </Flex>

      <Box
        sx={{
          backgroundColor: theme.colors.gray[50],
          padding: 3,
          borderRadius: 'md',
          border: '1px solid',
          borderColor: theme.colors.gray[200],
        }}
      >
        <PositionParametersContext.Provider value={contextValue}>
          <DataTable columns={columns} data={data} />
        </PositionParametersContext.Provider>
      </Box>
    </Box>
  );
};

export default PositionParametersView;
